import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Loader2, Gift, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AUDIENCES = [
  { id: 'buyers', label: 'All ticket buyers' },
  { id: 'checked_in', label: 'Checked-in attendees' },
  { id: 'waitlist', label: 'Still on the waitlist' },
];

export function PromoDrops({ eventId, walletBalanceCents, onSent }) {
  const [drops, setDrops] = useState(null);
  const [audience, setAudience] = useState('buyers');
  const [count, setCount] = useState(null);
  const [amount, setAmount] = useState('5.00');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setDrops((await axios.get(`${API}/events/${eventId}/promo-drops`, auth())).data.drops); }
    catch { /* ignore */ }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setCount(null);
    axios.get(`${API}/events/${eventId}/promo-drops/audience`, { params: { audience }, ...auth() })
      .then(r => setCount(r.data.count)).catch(() => setCount(0));
  }, [eventId, audience]);

  const cents = Math.round(parseFloat(amount || '0') * 100);
  const totalCents = (count || 0) * (cents || 0);

  const send = async () => {
    if (!cents || cents < 100) return toast.error('Minimum credit is $1.00 per person');
    if (!count) return toast.error('Nobody in that audience yet');
    if (totalCents > (walletBalanceCents || 0)) return toast.error(`This drop costs ${usd(totalCents)} — not enough in your wallet`);
    if (!window.confirm(`Send ${usd(cents)} to ${count} people (${usd(totalCents)} total from your wallet)?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/promo-drops`, { audience, amount_cents: cents, message }, auth());
      toast.success(`Credit dropped to ${r.data.recipient_count} people — emails on the way`);
      setMessage('');
      load(); onSent && onSent();
    } catch (e) { toast.error(e.response?.data?.detail || 'Drop failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="promo-drops">
      <Card className="border-amber-500/30"><CardContent className="py-3 space-y-2">
        <p className="text-xs font-semibold flex items-center gap-1.5"><Gift className="h-3.5 w-3.5 text-amber-400" /> Wallet promo drop</p>
        <p className="text-[10px] text-muted-foreground">Send everyone in an audience a small wallet credit — it spends itself at your next checkout. Costs only what you drop.</p>
        <div className="flex flex-wrap items-center gap-2">
          <select className="h-8 text-xs rounded-md border border-border bg-background px-2" value={audience}
            onChange={e => setAudience(e.target.value)} data-testid="drop-audience-select">
            {AUDIENCES.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
          <Badge variant="outline" className="text-[10px]" data-testid="drop-audience-count">
            <Users className="h-3 w-3 mr-1" /> {count === null ? '…' : `${count} people`}
          </Badge>
          <Input className="h-8 text-xs w-24" type="number" min="1" step="0.01" value={amount}
            onChange={e => setAmount(e.target.value)} data-testid="drop-amount-input" />
          <span className="text-[11px] text-muted-foreground">each{count > 0 && cents >= 100 ? ` = ${usd(totalCents)} total` : ''}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input className="h-8 text-xs flex-1 min-w-[200px]" placeholder='Message, e.g. "Thanks for coming — see you at the next one!"'
            value={message} onChange={e => setMessage(e.target.value)} data-testid="drop-message-input" />
          <Button size="sm" className="h-8 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
            disabled={busy || !count || !cents} onClick={send} data-testid="drop-send-btn">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Gift className="h-3 w-3 mr-1" /> Drop credit</>}
          </Button>
        </div>
      </CardContent></Card>

      {drops && drops.length > 0 && (
        <div className="rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="drops-table">
            <thead><tr className="bg-muted/40">
              <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Audience</th>
              <th className="text-right px-3 py-2">Sent</th><th className="text-right px-3 py-2">Cost</th>
              <th className="text-right px-3 py-2">Redeemed</th>
            </tr></thead>
            <tbody>
              {drops.map(d => (
                <tr key={d.drop_id} className="border-t border-border/60">
                  <td className="px-3 py-1.5 whitespace-nowrap">{String(d.created_at || '').slice(0, 10)}</td>
                  <td className="px-3 py-1.5 text-xs">{AUDIENCES.find(a => a.id === d.audience)?.label || d.audience}{d.message ? ` — "${d.message}"` : ''}</td>
                  <td className="px-3 py-1.5 text-right">{d.recipient_count} × {usd(d.amount_cents)}</td>
                  <td className="px-3 py-1.5 text-right text-rose-400 font-semibold">{usd(d.total_cents)}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-400 font-semibold">{usd(d.redeemed_cents)} ({d.total_cents ? Math.round((d.redeemed_cents / d.total_cents) * 100) : 0}%)</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
